import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '@material-ui/core/Button';
import GetAppIcon from '@material-ui/icons/GetApp';
import client from '../Contentful_API/api';
import ArticleLayout, { ArticleFallback } from '../Common/ArticleLayout';
import { LoadingState } from '../Common/PageLayout';
import { assetUrl, formatDate, toExcerpt } from '../Common/useEntries';

const SITE = 'https://www.mof.gov.tl';

/** Detail page for a single publication, with its download when available. */
export default function SinglePublication({ match }) {
    const slug = match?.params?.slug;
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);

        client
            .getEntries({ content_type: 'publications', 'fields.slug': slug, limit: 1 })
            .then((response) => {
                if (!active) return;
                setEntry(response.items?.[0] || null);
                setLoading(false);
            })
            .catch(() => {
                if (!active) return;
                setEntry(null);
                setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [slug]);

    if (loading) return <LoadingState label="Loading publication" />;

    if (!entry) {
        return (
            <ArticleFallback
                title="This publication could not be found"
                description="The document you requested is not available. It may have been replaced by a newer edition. Browse all publications to find the current version."
            />
        );
    }

    const fields = entry.fields || {};
    const cover = assetUrl(fields.publicationmedia || fields.cover || fields.image);
    const download = assetUrl(fields.publicationfile || fields.document || fields.attachment);
    const published = formatDate(fields.date || entry.sys?.createdAt);
    const body = fields.description || fields.body || '';

    return (
        <>
            <Helmet defer={false}>
                <title>{`${fields.title} | Ministry of Finance, Timor-Leste`}</title>
                <meta name="description" content={toExcerpt(body, 160)} />
                <meta property="og:title" content={fields.title} />
                {cover && <meta property="og:image" content={cover} />}
            </Helmet>

            <ArticleLayout
                eyebrow={fields.publicationCategory || 'Publication'}
                title={fields.title}
                breadcrumbs={[
                    { label: 'Publications', to: '/publications' },
                    { label: fields.title },
                ]}
                image={cover}
                imageAlt={`Cover of ${fields.title}`}
                body={body}
                details={[
                    { label: 'Published', value: published },
                    { label: 'Category', value: fields.publicationCategory },
                ]}
                shareUrl={`${SITE}/publicationdetails/${fields.slug}`}
                sidebar={
                    download ? (
                        <div style={{ marginBottom: 24 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                startIcon={<GetAppIcon />}
                                href={download}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download document
                            </Button>
                        </div>
                    ) : null
                }
            />
        </>
    );
}
